import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  CircularProgress
} from '@mui/material';

import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ProcurementIcon,
  Factory as ProductionIcon,
  PointOfSale as SalesIcon,
  LocalShipping as DispatchIcon,
  CurrencyRupee as FinanceIcon,
  People as HRIcon,
  EventNote as AttendanceIcon,
  AccessTime as ShiftIcon,
  Assessment as ReportsIcon,
  AdminPanelSettings as UsersIcon,
  Security as PermissionsIcon,
  GetApp as AttendanceReportsIcon,
  Percent as GSTIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  DirectionsCar as VehicleIcon,
  Route as TripIcon,
  Analytics as AnalyticsIcon,  // Add this for Executive Dashboard
  Logout,
} from '@mui/icons-material';

import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/slices/authSlice';

const drawerWidth = 240;

const allMenuItems = [
  {
    id: 'dashboard',
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard'
  },
  {
    id: 'executive-dashboard',
    text: 'Executive Dashboard',
    icon: <AnalyticsIcon />,
    path: '/executive-dashboard'
  },
  {
    id: 'inventory',
    text: 'Inventory',
    icon: <InventoryIcon />,
    path: '/inventory'
  },
  {
    id: 'procurement',
    text: 'Procurement',
    icon: <ProcurementIcon />,
    path: '/procurement'
  },
  {
    id: 'production',
    text: 'Production',
    icon: <ProductionIcon />,
    path: '/production'
  },
  {
    id: 'production-settings',
    text: 'Production Settings',
    icon: <SettingsIcon />,
    path: '/production-settings'
  },
  {
    id: 'sales',
    text: 'Sales',
    icon: <SalesIcon />,
    path: '/sales'
  },
  {
    id: 'dispatch',
    text: 'Dispatch',
    icon: <DispatchIcon />,
    path: '/dispatch'
  },
  {
    id: 'vehicle-config',
    text: 'Vehicle Configuration',
    icon: <VehicleIcon />,
    path: '/vehicle-config'
  },
  {
    id: 'trip-management',
    text: 'Trip Management',
    icon: <TripIcon />,
    path: '/trip-management'
  },
  {
    id: 'finance',
    text: 'Finance',
    icon: <FinanceIcon />,
    path: '/finance'
  },
  {
    id: 'hr',
    text: 'HR',
    icon: <HRIcon />,
    path: '/hr'
  },
  {
    id: 'attendance',
    text: 'Attendance',
    icon: <AttendanceIcon />,
    path: '/attendance'
  },
  {
    id: 'bulk-attendance',
    text: 'Bulk Attendance',
    icon: <AttendanceReportsIcon />,
    path: '/bulk-attendance'
  },
  {
    id: 'shifts',
    text: 'Shifts',
    icon: <ShiftIcon />,
    path: '/shifts'
  },
  {
    id: 'attendance-reports',
    text: 'Attendance Reports',
    icon: <ReportsIcon />,
    path: '/attendance-reports'
  },
  {
    id: 'gst',
    text: 'GST Rates',
    icon: <GSTIcon />,
    path: '/gst'
  },
  {
    id: 'payment-status',
    text: 'Payment Status',
    icon: <PaymentIcon />,
    path: '/payment-status'
  },
  {
    id: 'users',
    text: 'Users',
    icon: <UsersIcon />,
    path: '/users'
  },
  {
    id: 'permissions',
    text: 'Permissions',
    icon: <PermissionsIcon />,
    path: '/permissions'
  },
  {
    id: 'reports',
    text: 'Reports',
    icon: <ReportsIcon />,
    path: '/reports'
  },
];

// Define role-based menu access
const ROLE_MENUS = {
  super_admin: [
    'dashboard',
    'executive-dashboard',
    'inventory',
    'procurement',
    'production',
    'production-settings',
    'sales',
    'dispatch',
    'vehicle-config',
    'trip-management',
    'finance',
    'hr',
    'attendance',
    'bulk-attendance',
    'shifts',
    'attendance-reports',
    'gst',
    'users',
    'permissions',
    'reports',
    'payment-status'
  ],

  master_admin: [
    'dashboard',
    'executive-dashboard',
    'inventory',
    'procurement',
    'production',
    'production-settings',
    'sales',
    'dispatch',
    'vehicle-config',
    'trip-management',
    'finance',
    'hr',
    'attendance',
    'bulk-attendance',
    'shifts',
    'attendance-reports',
    'gst',
    'users',
    'reports',
    'payment-status'
  ],

  manager: [
    'dashboard',
    'executive-dashboard',
    'inventory',
    'procurement',
    'production',
    'production-settings',
    'sales',
    'dispatch',
    'vehicle-config',
    'trip-management',
    'finance',
    'hr',
    'attendance',
    'bulk-attendance',
    'shifts',
    'attendance-reports',
    'gst',
    'reports',
    'payment-status'
  ],

  supervisor: [
    'dashboard',
    'executive-dashboard',
    'inventory',
    'production',
    'dispatch',
    'vehicle-config',
    'trip-management',
    'hr',
    'attendance',
    'bulk-attendance',
    'shifts',
    'attendance-reports',
    'gst',
    'reports'
  ],

  accountant: [
    'dashboard',
    'executive-dashboard',
    'procurement',
    'sales',
    'finance',
    'reports',
    'attendance-reports',
    'gst',
    'payment-status'
  ],

  worker: [
    'dashboard',
    'executive-dashboard',
    'inventory',
    'production',
    'attendance'
  ],

  driver: [
    'dashboard',
    'executive-dashboard',
    'dispatch',
    'trip-management'
  ]
};

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userRole, setUserRole] = useState('worker');
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (user) {
      loadMenuItems();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadMenuItems = () => {
    setLoading(true);

    try {
      const userType =
        user?.user_type || 'worker';

      setUserRole(userType);

      const allowedIds =
        ROLE_MENUS[userType] || ['dashboard'];

      const filtered =
        allMenuItems.filter(
          (item) =>
            allowedIds.includes(item.id)
        );

      setMenuItems(filtered);
    } catch (error) {
      console.error(
        'Error loading menu items:',
        error
      );

      setMenuItems(
        allMenuItems.filter(
          (item) =>
            item.id === 'dashboard'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDrawerToggle = () =>
    setMobileOpen(!mobileOpen);

  const handleMenuOpen = (e) =>
    setAnchorEl(e.currentTarget);

  const handleMenuClose = () =>
    setAnchorEl(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const drawer = (
    <Box>
      <Toolbar>
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            color: '#2E7D32'
          }}
        >
          Sri Shakthi Traders
        </Typography>
      </Toolbar>

      <Divider />

      <List>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.id}
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
            }}
            sx={{
              '&:hover': {
                backgroundColor:
                  '#e8f5e9'
              }
            }}
          >
            <ListItemIcon
              sx={{
                color: '#2E7D32'
              }}
            >
              {item.icon}
            </ListItemIcon>

            <ListItemText
              primary={item.text}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) =>
            theme.zIndex.drawer + 1,
          backgroundColor: '#2E7D32'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={
              handleDrawerToggle
            }
            sx={{
              mr: 2,
              display: {
                sm: 'none'
              }
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 'bold'
            }}
          >
            Sri Shakthi Traders
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mr: 2,
              display: {
                xs: 'none',
                sm: 'block'
              }
            }}
          >
            {userRole
              ?.replace('_', ' ')
              .toUpperCase()}
          </Typography>

          <IconButton
            onClick={handleMenuOpen}
          >
            <Avatar
              sx={{
                bgcolor: '#FF8F00'
              }}
            >
              {user?.username
                ?.charAt(0)
                .toUpperCase()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => {
                navigate('/dashboard');
                handleMenuClose();
              }}
            >
              Dashboard
            </MenuItem>

            <MenuItem
              onClick={() => {
                navigate('/executive-dashboard');
                handleMenuClose();
              }}
            >
              Executive Dashboard
            </MenuItem>

            <MenuItem
              onClick={() => {
                navigate('/reports');
                handleMenuClose();
              }}
            >
              Reports
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={handleLogout}
            >
              <Logout
                sx={{ mr: 1 }}
              />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: {
            sm: drawerWidth
          },
          flexShrink: {
            sm: 0
          }
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={
            handleDrawerToggle
          }
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: {
              xs: 'block',
              sm: 'none'
            },
            '& .MuiDrawer-paper': {
              width: drawerWidth
            }
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: {
              xs: 'none',
              sm: 'block'
            },
            '& .MuiDrawer-paper': {
              width: drawerWidth
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={(theme) => ({
          flexGrow: 1,
          p: 3,
          width: {
            sm: `calc(100% - ${drawerWidth}px)`
          },
          mt: 8,
          backgroundColor: '#f5f5f5',
          minHeight: '100vh'
        })}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;